/**
 * 입출입 기록 데모 페이지
 * 간단한 Mock 데이터로 UI만 시연
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClockIcon,
  MagnifyingGlassIcon,
  ArrowRightOnRectangleIcon,
  ArrowLeftOnRectangleIcon,
  CalendarIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import DemoNavigation from '../components/DemoNavigation';
import { getTodayKST } from '../utils/dateUtils';

// Mock 데이터
const MOCK_LOGS = [
  {
    id: '1',
    studentName: '김철수',
    type: 'entry',
    timestamp: '2025-01-24 09:15:23',
    gate: '정문',
    method: '얼굴인식',
  },
  {
    id: '2',
    studentName: '이영희',
    type: 'entry',
    timestamp: '2025-01-24 09:22:45',
    gate: '정문',
    method: '카드',
  },
  {
    id: '3',
    studentName: '박민수',
    type: 'exit',
    timestamp: '2025-01-24 12:30:12',
    gate: '정문',
    method: '얼굴인식',
  },
  {
    id: '4',
    studentName: '정수진',
    type: 'entry',
    timestamp: '2025-01-24 13:05:33',
    gate: '후문',
    method: '얼굴인식',
  },
  {
    id: '5',
    studentName: '최동욱',
    type: 'exit',
    timestamp: '2025-01-24 15:45:20',
    gate: '정문',
    method: '카드',
  },
  {
    id: '6',
    studentName: '강은지',
    type: 'entry',
    timestamp: '2025-01-24 08:50:11',
    gate: '정문',
    method: '얼굴인식',
  },
  {
    id: '7',
    studentName: '윤서준',
    type: 'entry',
    timestamp: '2025-01-24 10:15:44',
    gate: '후문',
    method: '얼굴인식',
  },
  {
    id: '8',
    studentName: '한지민',
    type: 'exit',
    timestamp: '2025-01-24 17:20:55',
    gate: '정문',
    method: '카드',
  },
  {
    id: '9',
    studentName: '김철수',
    type: 'exit',
    timestamp: '2025-01-24 18:05:30',
    gate: '정문',
    method: '얼굴인식',
  },
  {
    id: '10',
    studentName: '이영희',
    type: 'exit',
    timestamp: '2025-01-24 19:30:22',
    gate: '정문',
    method: '얼굴인식',
  },
];

const DemoEntranceExitPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterGate, setFilterGate] = useState('all');
  const [selectedDate, setSelectedDate] = useState(getTodayKST());

  // 필터링된 로그
  const filteredLogs = MOCK_LOGS.filter((log) => {
    const matchesSearch = log.studentName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || log.type === filterType;
    const matchesGate = filterGate === 'all' || log.gate === filterGate;
    return matchesSearch && matchesType && matchesGate;
  });

  // 통계
  const stats = {
    total: MOCK_LOGS.length,
    entry: MOCK_LOGS.filter((l) => l.type === 'entry').length,
    exit: MOCK_LOGS.filter((l) => l.type === 'exit').length,
    currentInside: MOCK_LOGS.filter((l) => l.type === 'entry').length - MOCK_LOGS.filter((l) => l.type === 'exit').length,
  };

  const getTypeIcon = (type: string) => {
    return type === 'entry' ? (
      <ArrowRightOnRectangleIcon className="h-5 w-5 text-green-600" />
    ) : (
      <ArrowLeftOnRectangleIcon className="h-5 w-5 text-red-600" />
    );
  };

  const getTypeText = (type: string) => {
    return type === 'entry' ? '입실' : '퇴실';
  };

  const getTypeBadge = (type: string) => {
    return type === 'entry'
      ? 'bg-green-100 text-green-800'
      : 'bg-red-100 text-red-800';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 데모 헤더 */}
      <div className="bg-blue-600 text-white py-3 px-4 text-center">
        <p className="text-sm">
          📢 이것은 데모 페이지입니다. 모든 데이터는 가상 데이터이며, 데모 시연을 위해 주기적으로 업데이트되어 조금씩 변동될 수 있습니다.
        </p>
      </div>

      {/* 네비게이션 헤더 */}
      <DemoNavigation currentTitle="입출입 기록 (데모)" currentIcon={ClockIcon} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 안내 메시지 */}
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            💡 학생들의 입실/퇴실 기록을 실시간으로 확인하고 검색할 수 있습니다!
          </p>
        </div>

        {/* 통계 카드 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">전체 기록</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}건</p>
              </div>
              <ClockIcon className="h-12 w-12 text-blue-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">입실</p>
                <p className="text-2xl font-bold text-green-600">{stats.entry}건</p>
              </div>
              <ArrowRightOnRectangleIcon className="h-12 w-12 text-green-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">퇴실</p>
                <p className="text-2xl font-bold text-red-600">{stats.exit}건</p>
              </div>
              <ArrowLeftOnRectangleIcon className="h-12 w-12 text-red-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">현재 재실</p>
                <p className="text-2xl font-bold text-purple-600">{stats.currentInside}명</p>
              </div>
              <ClockIcon className="h-12 w-12 text-purple-500" />
            </div>
          </div>
        </div>

        {/* 검색 및 필터 */}
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* 날짜 선택 */}
            <div className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 text-gray-400" />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* 검색 */}
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="학생 이름으로 검색..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* 타입 필터 */}
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">전체</option>
              <option value="entry">입실만</option>
              <option value="exit">퇴실만</option>
            </select>

            {/* 게이트 필터 */}
            <select
              value={filterGate}
              onChange={(e) => setFilterGate(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">전체 게이트</option>
              <option value="정문">정문</option>
              <option value="후문">후문</option>
            </select>

            {/* 엑셀 다운로드 */}
            <button
              onClick={() => alert('데모 모드에서는 다운로드가 제한됩니다.')}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              엑셀
            </button>
          </div>
        </div>

        {/* 입출입 기록 테이블 */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  구분
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  학생명
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  시간
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  게이트
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  인증 방법
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredLogs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {getTypeIcon(log.type)}
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getTypeBadge(log.type)}`}
                      >
                        {getTypeText(log.type)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{log.studentName}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{log.timestamp}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                      {log.gate}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{log.method}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredLogs.length === 0 && (
            <div className="text-center py-12">
              <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">기록 없음</h3>
              <p className="mt-1 text-sm text-gray-500">해당 조건에 맞는 기록이 없습니다.</p>
            </div>
          )}
        </div>

        {/* 결과 개수 */}
        <div className="mt-4 text-sm text-gray-600 text-center">
          총 {filteredLogs.length}건의 기록이 검색되었습니다.
        </div>
      </div>
    </div>
  );
};

export default DemoEntranceExitPage;
